// src/context/CoFounderContext.js
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import {
  collection, doc, addDoc, updateDoc, getDocs, deleteDoc,
  query, where, orderBy, limit, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { AppContext } from './AppContext';
import { createProvider } from '../services/ai';
import { buildSystemPrompt } from '../services/cofounder/systemPrompt';
import { getToolRegistry } from '../services/cofounder/tools';
import { executeToolCall } from '../services/cofounder/tools/toolExecutor';

const CoFounderContext = createContext();

export function useCoFounder() {
  return useContext(CoFounderContext);
}

export function CoFounderProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const { activeBusiness } = useContext(AppContext);

  // Chat state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Insights state
  const [insights, setInsights] = useState([]);
  const [unreadInsightCount, setUnreadInsightCount] = useState(0);

  // Actions state
  const [pendingActions, setPendingActions] = useState([]);
  const [allActions, setAllActions] = useState([]);
  const [unreadActionCount, setUnreadActionCount] = useState(0);

  // Page context
  const [currentPageContext, setCurrentPageContext] = useState('');

  // Config
  const [agentConfig, setAgentConfig] = useState({
    provider: 'cloud-function',
    persona: 'professional',
    enableProactiveInsights: true,
    enabledToolCategories: ['read', 'write']
  });

  // Onboarding
  const [hasOnboarded, setHasOnboarded] = useState(false);

  const providerRef = useRef(null);

  // Initialize provider
  useEffect(() => {
    providerRef.current = createProvider(agentConfig.provider);
  }, [agentConfig.provider]);

  // Load conversations on mount
  useEffect(() => {
    if (currentUser?.uid) {
      loadConversations();
      loadInsights();
      loadActions();
      checkOnboarding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const checkOnboarding = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const configRef = doc(db, 'cofounderConfig', currentUser.uid);
      const { getDoc } = await import('firebase/firestore');
      const configDoc = await getDoc(configRef);
      setHasOnboarded(configDoc.exists());
    } catch (e) {
      setHasOnboarded(false);
    }
  }, [currentUser?.uid]);

  const completeOnboarding = useCallback(async (config = {}) => {
    if (!currentUser?.uid) return;
    try {
      const { setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, 'cofounderConfig', currentUser.uid), {
        userId: currentUser.uid,
        ...agentConfig,
        ...config,
        completedAt: serverTimestamp()
      });
      setAgentConfig(prev => ({ ...prev, ...config }));
      setHasOnboarded(true);
    } catch (e) {
      console.error('Error saving onboarding config:', e);
    }
  }, [currentUser?.uid, agentConfig]);

  const loadConversations = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const q = query(
        collection(db, 'cofounderChats'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const convos = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setConversations(convos);
    } catch (e) {
      console.error('Error loading conversations:', e);
    }
  }, [currentUser?.uid]);

  const loadInsights = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const q = query(
        collection(db, 'cofounderInsights'),
        where('userId', '==', currentUser.uid),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setInsights(items);
      setUnreadInsightCount(items.filter(i => !i.readAt).length);
    } catch (e) {
      console.error('Error loading insights:', e);
    }
  }, [currentUser?.uid]);

  const loadActions = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const q = query(
        collection(db, 'cofounderActions'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
      setAllActions(items);
      setUnreadActionCount(items.filter(a => a.status === 'pending').length);
    } catch (e) {
      console.error('Error loading actions:', e);
    }
  }, [currentUser?.uid]);

  const startNewConversation = useCallback(async () => {
    if (!currentUser?.uid) return null;
    try {
      const newConvo = {
        userId: currentUser.uid,
        businessId: activeBusiness?.id || null,
        title: 'New conversation',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(collection(db, 'cofounderChats'), newConvo);
      const convo = { id: docRef.id, ...newConvo };
      setConversations(prev => [convo, ...prev]);
      setActiveConversationId(docRef.id);
      setMessages([]);
      return docRef.id;
    } catch (e) {
      console.error('Error creating conversation:', e);
      return null;
    }
  }, [currentUser?.uid, activeBusiness?.id]);

  const loadConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      const q = query(
        collection(db, 'cofounderChats', conversationId, 'messages'),
        orderBy('createdAt', 'asc')
      );
      const snapshot = await getDocs(q);
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(msgs);
      setActiveConversationId(conversationId);
    } catch (e) {
      console.error('Error loading messages:', e);
    }
  }, []);

  const selectConversation = useCallback(async (conversationId) => {
    setActiveConversationId(conversationId);
    await loadConversationMessages(conversationId);
  }, [loadConversationMessages]);

  const deleteConversation = useCallback(async (conversationId) => {
    try {
      await deleteDoc(doc(db, 'cofounderChats', conversationId));
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConversationId === conversationId) {
        setActiveConversationId(null);
        setMessages([]);
      }
    } catch (e) {
      console.error('Error deleting conversation:', e);
    }
  }, [activeConversationId]);

  const renameConversation = useCallback(async (conversationId, newTitle) => {
    try {
      await updateDoc(doc(db, 'cofounderChats', conversationId), { title: newTitle });
      setConversations(prev =>
        prev.map(c => c.id === conversationId ? { ...c, title: newTitle } : c)
      );
    } catch (e) {
      console.error('Error renaming conversation:', e);
    }
  }, []);

  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isLoading) return;

    let convoId = activeConversationId;
    if (!convoId) {
      convoId = await startNewConversation();
      if (!convoId) return;
    }

    // Add user message
    const userMessage = {
      role: 'user',
      content,
      createdAt: Timestamp.now()
    };

    try {
      const userMsgRef = await addDoc(
        collection(db, 'cofounderChats', convoId, 'messages'),
        userMessage
      );
      const userMsg = { id: userMsgRef.id, ...userMessage };
      setMessages(prev => [...prev, userMsg]);
    } catch (e) {
      console.error('Error saving user message:', e);
    }

    setIsLoading(true);

    try {
      const tools = getToolRegistry();
      const systemPrompt = buildSystemPrompt({
        business: activeBusiness,
        userProfile,
        pageContext: currentPageContext,
        tools
      });

      // Build message history for API
      const apiMessages = [
        { role: 'system', content: systemPrompt },
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user', content }
      ];

      const provider = providerRef.current;
      let response = await provider.chat(apiMessages, tools);

      // Handle tool calls (loop for multi-step)
      let iterations = 0;
      while (response.toolCalls && iterations < 5) {
        iterations++;
        const toolResults = [];

        for (const toolCall of response.toolCalls) {
          const tool = tools.find(t => t.name === toolCall.name);
          if (!tool) {
            toolResults.push({ name: toolCall.name, result: 'Tool not found' });
            continue;
          }

          if (tool.requiresConfirmation) {
            // Add pending action
            const actionId = `action_${Date.now()}_${Math.random().toString(36).slice(2)}`;
            const pendingAction = {
              id: actionId,
              toolName: toolCall.name,
              toolDescription: tool.description,
              args: toolCall.args,
              status: 'pending',
              conversationId: convoId
            };
            setPendingActions(prev => [...prev, pendingAction]);

            // Save to Firestore
            try {
              const actionDocRef = await addDoc(collection(db, 'cofounderActions'), {
                userId: currentUser.uid,
                businessId: activeBusiness?.id || null,
                ...pendingAction,
                createdAt: serverTimestamp()
              });
              pendingAction.firestoreId = actionDocRef.id;
            } catch (e) {
              console.error('Error saving action:', e);
            }

            toolResults.push({
              name: toolCall.name,
              result: `Action "${toolCall.name}" requires user confirmation. Waiting for approval.`
            });
          } else {
            // Execute read-only tools immediately
            try {
              const result = await executeToolCall(toolCall.name, toolCall.args, {
                userProfile,
                activeBusiness,
                currentUser
              });
              toolResults.push({ name: toolCall.name, result: JSON.stringify(result) });
            } catch (e) {
              toolResults.push({ name: toolCall.name, result: `Error: ${e.message}` });
            }
          }
        }

        // Feed tool results back to the AI
        const toolResultMessage = toolResults
          .map(r => `[Tool: ${r.name}]\n${r.result}`)
          .join('\n\n');

        apiMessages.push({ role: 'assistant', content: response.content || '(using tools)' });
        apiMessages.push({ role: 'user', content: `Tool results:\n${toolResultMessage}` });

        response = await provider.chat(apiMessages, tools);
      }

      // Save assistant response
      const assistantMessage = {
        role: 'assistant',
        content: response.content || 'I processed your request.',
        createdAt: Timestamp.now()
      };

      try {
        const asstMsgRef = await addDoc(
          collection(db, 'cofounderChats', convoId, 'messages'),
          assistantMessage
        );
        setMessages(prev => [...prev, { id: asstMsgRef.id, ...assistantMessage }]);
      } catch (e) {
        console.error('Error saving assistant message:', e);
      }

      // Update conversation title if it's the first message
      if (messages.length === 0) {
        const title = content.length > 50 ? content.substring(0, 50) + '...' : content;
        try {
          await updateDoc(doc(db, 'cofounderChats', convoId), {
            title,
            updatedAt: serverTimestamp()
          });
          setConversations(prev =>
            prev.map(c => c.id === convoId ? { ...c, title } : c)
          );
        } catch (e) {
          console.error('Error updating title:', e);
        }
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = {
        role: 'assistant',
        content: `I'm sorry, I encountered an error: ${error.message}. Please try again.`,
        createdAt: Timestamp.now(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [activeConversationId, messages, isLoading, activeBusiness, userProfile, currentPageContext, currentUser, startNewConversation]);

  const confirmAction = useCallback(async (actionId) => {
    const action = pendingActions.find(a => a.id === actionId)
      || allActions.find(a => a.id === actionId);
    if (!action) return;

    setPendingActions(prev =>
      prev.map(a => a.id === actionId ? { ...a, status: 'executing' } : a)
    );

    try {
      const result = await executeToolCall(action.toolName, action.args, {
        userProfile,
        activeBusiness,
        currentUser
      });

      setPendingActions(prev =>
        prev.map(a => a.id === actionId ? { ...a, status: 'completed', result } : a)
      );

      // Update Firestore record
      if (action.firestoreId) {
        try {
          await updateDoc(doc(db, 'cofounderActions', action.firestoreId), {
            status: 'completed',
            result: JSON.stringify(result),
            completedAt: serverTimestamp()
          });
        } catch (e) {
          console.error('Error updating action in Firestore:', e);
        }
      }

      // Add confirmation message to chat
      const confirmMsg = {
        role: 'assistant',
        content: `Action **${action.toolName}** completed successfully.`,
        createdAt: Timestamp.now()
      };
      setMessages(prev => [...prev, confirmMsg]);

      loadActions();
    } catch (e) {
      setPendingActions(prev =>
        prev.map(a => a.id === actionId ? { ...a, status: 'failed', error: e.message } : a)
      );

      // Update Firestore record on failure
      if (action.firestoreId) {
        try {
          await updateDoc(doc(db, 'cofounderActions', action.firestoreId), {
            status: 'failed',
            error: e.message,
            failedAt: serverTimestamp()
          });
        } catch (err) {
          console.error('Error updating failed action:', err);
        }
      }

      loadActions();
    }
  }, [pendingActions, allActions, userProfile, activeBusiness, currentUser, loadActions]);

  const rejectAction = useCallback(async (actionId) => {
    const action = pendingActions.find(a => a.id === actionId)
      || allActions.find(a => a.id === actionId);

    setPendingActions(prev =>
      prev.map(a => a.id === actionId ? { ...a, status: 'rejected' } : a)
    );

    if (action?.firestoreId) {
      try {
        await updateDoc(doc(db, 'cofounderActions', action.firestoreId), {
          status: 'rejected',
          rejectedAt: serverTimestamp()
        });
      } catch (e) {
        console.error('Error updating rejected action:', e);
      }
    }

    loadActions();
  }, [pendingActions, allActions, loadActions]);

  const dismissInsight = useCallback(async (insightId) => {
    try {
      await updateDoc(doc(db, 'cofounderInsights', insightId), {
        status: 'dismissed',
        dismissedAt: serverTimestamp()
      });
      setInsights(prev => prev.filter(i => i.id !== insightId));
      setUnreadInsightCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Error dismissing insight:', e);
    }
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const setPageContext = useCallback((context) => {
    setCurrentPageContext(context);
  }, []);

  const value = {
    // Chat
    conversations,
    activeConversationId,
    messages,
    isLoading,
    sendMessage,
    startNewConversation,
    selectConversation,
    deleteConversation,
    renameConversation,
    loadConversationMessages,

    // UI
    isOpen,
    setIsOpen,
    isExpanded,
    setIsExpanded,
    togglePanel,

    // Insights
    insights,
    unreadInsightCount,
    dismissInsight,
    loadInsights,

    // Actions
    pendingActions,
    allActions,
    unreadActionCount,
    confirmAction,
    rejectAction,
    loadActions,

    // Config
    agentConfig,
    setAgentConfig,
    currentPageContext,
    setPageContext,

    // Onboarding
    hasOnboarded,
    completeOnboarding
  };

  return (
    <CoFounderContext.Provider value={value}>
      {children}
    </CoFounderContext.Provider>
  );
}
